import {
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Spin,
  Steps,
  StepsProps,
} from "@gw/web-basic-components";
import React, { useEffect, useRef, useState } from "react";
import TableEditor, {
  Field,
  TableEditorConfig,
  TableEditorProps,
  TableEditorRef,
  TableViewerProps,
  TableViewerRef,
} from "./table-editor";
import {
  NameCheckReq,
  ParamTreeReq,
  SettlementEditorProps,
  SettlementPreviewProps,
  SettlementTemplateReq,
  SettlementViewerProps,
  SettlementViewerRef,
  TemplateidReq,
  TemplateidRes,
  TemplatePreviewReq,
  TemplatePreviewRes,
} from "./dto";
import { useRequest, apis } from "../utils";
import Preview from "./preview";
import Viewer from "./viewer";
import useFormulaData from "./hooks/use-formula-data";

const items: StepsProps["items"] = [
  {
    title: "基本设置",
  },
  {
    title: "模版配置",
  },
];

const SettlementEditor = ({
  open,
  copy = false,
  onOk,
  onClose,
  id,
  tenantId,
  ...drawerProps
}: SettlementEditorProps) => {
  const [current, setCurrent] = useState(0);
  const [preview, setPreview] = useState(false);
  const [fields, setFields] = useState<Field[]>([]);
  const [form] = Form.useForm();

  const formSettlementMode = Form.useWatch("settlementMode", form);
  // 是否为上网&补贴
  const isProfit = ["gridProfit", "subsidyProfit"].includes(formSettlementMode);
  // 新增模版
  const [{ loading: fetchAddTemplateLoading }, fetchAddTemplate] = useRequest<
    SettlementTemplateReq,
    never
  >(apis.settlementTmpl.template);
  // 预览数据
  const [{ loading: previewLoading, data: previdwData }, fetchPreviewData] =
    useRequest<TemplatePreviewReq, TemplatePreviewRes>(
      apis.settlementTmpl.preview
    );
  // 模版详情
  const [
    { data: detail, loading: fetchTemplateDetailLoading },
    fetchTemplateDetail,
  ] = useRequest<TemplateidReq, TemplateidRes>(apis.settlementTmpl.show);
  // 更新模版
  const [{ loading: fetchUpdateTemplateLoading }, fetchUpdateTemplate] =
    useRequest<SettlementTemplateReq & TemplateidReq, never>(
      apis.settlementTmpl.update
    );
  // 获取字段
  const [{ loading: fetchFieldsLoading }, fetchFields] = useRequest<
    ParamTreeReq,
    Field[]
  >(apis.settlementTmpl.tree);
  // 校验模版是否重命名
  const [, fetchCheck] = useRequest<NameCheckReq, boolean>(
    apis.settlementTmpl.check
  );

  const [initialConfig, setInitialConfig] = useState<TableEditorConfig>();
  const [config, setConfig] = useState<TableEditorConfig>();

  // step在第二步时第一步的form会被销毁 此时获取不到form值 所以用ref保存
  const formValuesRef = useRef<
    Pick<
      SettlementTemplateReq,
      "templateName" | "settlementMode" | "printDirection"
    > &
      SettlementTemplateReq["electricityPriceInfo"]
  >();

  const tableEditorRef = useRef<TableEditorRef>();

  const isCopyAdd = id && copy;
  const isEdit = id && !copy;

  const fetchPreview = (cfg: TableEditorConfig, settlementMode: string) => {
    const currentFormulas = cfg?.formulas ?? [];
    fetchPreviewData({
      formulaList: currentFormulas.map((i) => ({
        code: i.code,
        name: i.name,
        type: "number",
        formula: i.value.map((j) => j.value).join(""),
        isList: i.isList ?? false,
      })),
      settlementMode,
    });
  };

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setCurrent(0);
    if (id) {
      fetchTemplateDetail({
        templateId: id,
      });
    }
  }, [open, id]);

  useEffect(() => {
    if (!detail) return;
    const {
      templateName,
      settlementMode,
      printDirection,
      electricityPriceInfo,
      templateContent,
    } = detail ?? {};
    form.setFieldsValue({
      printDirection,
      templateName: isCopyAdd ? `${templateName}_复制` : templateName,
      settlementMode,
      ...electricityPriceInfo,
    });
    try {
      const cfg = JSON.parse(templateContent) as TableEditorConfig;
      setInitialConfig(cfg);
      fetchPreview(cfg, settlementMode);
    } catch (e) {
      console.error("【SettlementEditor】解析模板内容失败", e);
    }
  }, [detail, copy, id]);

  const onSave = async (apply = false) => {
    const { templateName, settlementMode, printDirection, ...rest } =
      formValuesRef.current ?? {};
    const cfg = tableEditorRef.current?.getConfig();
    const data = {
      templateContent: JSON.stringify(cfg),
      enabled: apply ? 0 : 1,
      paramDetail: tableEditorRef.current?.getUsedFields()?.map((i) => ({
        ...i,
        formulaSource: i.formula,
        formula: i.formula.map((j) => j.value).join(""),
      })),
      templateName,
      settlementMode,
      printDirection,
      electricityPriceInfo: rest,
    };
    if (isEdit) {
      await fetchUpdateTemplate({
        ...detail,
        ...data,
        templateId: id,
      });
    } else {
      await fetchAddTemplate({
        ...(id ? detail : {}),
        ...data,
        type: 2,
        tenantId,
      });
    }
    onOk?.();
  };

  const withFormulasDataSource = useFormulaData(
    config,
    previdwData?.dataList ?? []
  );

  const getTips = () => {
    const { settlementMode, hasSolarSelfConsumption } =
      formValuesRef.current ?? {};
    const isPv = settlementMode === "pvConsumption";
    const tips = [
      "1、结算金额与结算电量为最终结算的核心依据，配置参数时请优先选择并准确配置。",
    ];
    if (isPv && hasSolarSelfConsumption) {
      tips.push(
        "2、如光伏存在自用电结算场景，请在模板中配置自用电相关参数，并在结算金额中予以扣减，同时在结算规则中选择并应用”含自用电“的结算模板。"
      );
    }
    return tips.join("");
  };

  return (
    <>
      <Drawer
        zIndex={800}
        destroyOnClose
        fullscreen
        getContainer={false}
        {...drawerProps}
        title={isEdit ? "编辑模版" : "新建模版"}
        open={open}
        onClose={onClose}
      >
        <div className="wbuc-size-full wbuc-p-5 [&_.ant-spin-container]:wbuc-size-full [&_.ant-spin-nested-loading]:wbuc-size-full">
          <Spin spinning={fetchTemplateDetailLoading}>
            <div className="wbuc-flex wbuc-size-full wbuc-flex-col wbuc-items-center wbuc-gap-6 wbuc-overflow-hidden">
              <div className="wbuc-relative wbuc-flex wbuc-w-full wbuc-flex-shrink-0 wbuc-justify-center">
                <Steps
                  items={items.map((item, idx) => ({
                    ...item,
                    // antd里应用自定义icon有bug 导致第一次正确后面重设current显示不正确 使用这种方式当current与idx相等时不应用自定义icon
                    icon: idx === current ? null : undefined,
                  }))}
                  current={current}
                  className="wbuc-w-72"
                />
                {current === 0 && (
                  <Button
                    type="primary"
                    onClick={async () => {
                      // 获取字段
                      const formValues = await form.validateFields();
                      formValuesRef.current = formValues;
                      const data = await fetchFields({
                        ...formValues,
                        electricityPriceTemplateType:
                          formValues.electricityPriceTemplateType?.join(","),
                        calculationMode: formValues.calculationMode?.join(","),
                      });
                      setFields(data);
                      // 跳转下一步
                      setCurrent(1);
                    }}
                    className="!wbuc-absolute wbuc-right-0 wbuc-top-0"
                    loading={fetchFieldsLoading}
                  >
                    下一步
                  </Button>
                )}
                {current === 1 && (
                  <div className="wbuc-absolute wbuc-right-0 wbuc-top-0 wbuc-flex wbuc-gap-5">
                    <Button
                      type="primary"
                      ghost
                      onClick={() => {
                        setInitialConfig(tableEditorRef.current?.getConfig());
                        setCurrent(0);
                      }}
                    >
                      上一步
                    </Button>
                    <Button
                      type="primary"
                      ghost
                      onClick={() => {
                        setConfig((prev) => {
                          const cfg = tableEditorRef.current?.getConfig();
                          const currentFormulas = cfg?.formulas ?? [];
                          const prevFormulasStr = JSON.stringify(
                            prev?.formulas ?? []
                          );
                          const currentFormulasStr =
                            JSON.stringify(currentFormulas);
                          if (prevFormulasStr !== currentFormulasStr) {
                            fetchPreview(
                              cfg,
                              formValuesRef.current?.settlementMode
                            );
                          }
                          return cfg;
                        });
                        setPreview(true);
                      }}
                    >
                      预览
                    </Button>
                    <Button
                      type="primary"
                      ghost
                      onClick={() => {
                        tableEditorRef.current?.openFormulaSetting();
                      }}
                    >
                      自定义参数
                    </Button>
                    <Button
                      type="primary"
                      ghost
                      loading={
                        fetchAddTemplateLoading || fetchUpdateTemplateLoading
                      }
                      onClick={() => onSave()}
                    >
                      保存不应用
                    </Button>
                    <Button
                      type="primary"
                      loading={
                        fetchAddTemplateLoading || fetchUpdateTemplateLoading
                      }
                      onClick={() => onSave(true)}
                    >
                      保存并应用
                    </Button>
                  </div>
                )}
              </div>
              {current === 0 && (
                <Form
                  className="wbuc-grid wbuc-w-full wbuc-grid-cols-4 wbuc-gap-x-6"
                  form={form}
                >
                  <Form.Item
                    name="templateName"
                    label="模版名称"
                    rules={[
                      {
                        required: true,
                        message: "请输入模版名称",
                        validateTrigger: "onChange",
                      },
                      {
                        validateTrigger: "onSubmit",
                        validator: async (_, v) => {
                          // 编辑时如果名称未改动则不校验
                          if (isEdit && v === detail?.templateName) {
                            return Promise.resolve();
                          }
                          const pass = await fetchCheck({
                            templateName: v,
                            tenantId: tenantId || detail?.tenantId,
                          });
                          if (pass) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("已存在同名模板，请修改名称后重试")
                          );
                        },
                      },
                    ]}
                    validateTrigger={["onChange", "onSubmit"]}
                  >
                    <Input size="large" placeholder="请输入模版名称" />
                  </Form.Item>
                  <Form.Item
                    name="settlementMode"
                    label="模版类型"
                    rules={[{ required: true, message: "请选择模版类型" }]}
                    initialValue="pvConsumption"
                  >
                    <Select
                      size="large"
                      placeholder="请选择模版类型"
                      options={[
                        { label: "光伏消纳", value: "pvConsumption" },
                        { label: "储能结算", value: "storageAllocation" },
                        { label: "充电用能结算", value: "cpCharge" },
                        { label: "上网结算", value: "gridProfit" },
                        { label: "补贴结算", value: "subsidyProfit" },
                      ]}
                    />
                  </Form.Item>
                  {!isProfit && (
                    <Form.Item
                      name="electricityPriceTemplateType"
                      label="适用电价"
                      rules={[{ required: true, message: "请输入适用电价" }]}
                    >
                      <Select
                        size="large"
                        options={[
                          { label: "固定电价", value: "weighted" },
                          { label: "电网代购分时电价", value: "stateGrid" },
                          { label: "自定义电价", value: "custom" },
                        ]}
                        placeholder="请选择适用电价"
                        mode="multiple"
                        maxTagCount={2}
                      />
                    </Form.Item>
                  )}
                  {formSettlementMode === "pvConsumption" && (
                    <Form.Item
                      name="hasSolarSelfConsumption"
                      label="是否包含光伏自用电结算单"
                      rules={[
                        {
                          required: true,
                          message: "请选择是否包含光伏自用电结算单",
                        },
                      ]}
                    >
                      <Select
                        size="large"
                        options={[
                          { label: "是", value: true },
                          { label: "否", value: false },
                        ]}
                        placeholder="请选择是否包含光伏自用电结算单"
                      />
                    </Form.Item>
                  )}
                  {!isProfit && (
                    <Form.Item
                      name="calculationMode"
                      label="适用计算模式"
                      rules={[
                        { required: true, message: "请选择适用计算模式" },
                      ]}
                      tooltip="指场站电表是否具备分时测示数的功能，该信息当前维护在场站系统的拓扑中，计算模式的选择会影响模板是否需要展示电表分时示数，若选择电价模板，则模板中只可选到电表本期/上期总数据；若选择分时测点，则模板中还可以选到电表的各分时示数数据；若都选择，当电表不支持分时测点则可能会出现分时示数都为空的情况，请根据实际业务选择该模式"
                    >
                      <Select
                        size="large"
                        options={[
                          { label: "电价模板", value: 1 },
                          { label: "分时测点", value: 2 },
                        ]}
                        placeholder="请选择适用计算模式"
                        mode="multiple"
                      />
                    </Form.Item>
                  )}
                  <Form.Item
                    name="printDirection"
                    label="模版布局"
                    rules={[{ required: true, message: "请选择模版布局" }]}
                    initialValue="portrait"
                  >
                    <Select
                      size="large"
                      options={[
                        { label: "横版", value: "landscape" },
                        { label: "竖版", value: "portrait" },
                      ]}
                      placeholder="请选择模版布局"
                    />
                  </Form.Item>
                  {formSettlementMode === "pvConsumption" && (
                    <Form.Item
                      name="isRealGradientPriceAndDiscount"
                      label="阶梯价格"
                      rules={[
                        {
                          required: true,
                          message: "请选择阶梯价格",
                        },
                      ]}
                    >
                      <Radio.Group
                        options={[
                          {
                            label: "是",
                            value: true,
                          },
                          {
                            label: "否",
                            value: false,
                          },
                        ]}
                      />
                    </Form.Item>
                  )}
                </Form>
              )}
              {current === 1 && (
                <div className="wbuc-flex wbuc-w-full wbuc-flex-1 wbuc-flex-col wbuc-gap-4 wbuc-overflow-hidden">
                  <div className="wbuc-bg-[#FF9B2A1A] wbuc-px-4 wbuc-py-3 wbuc-text-[1rem] wbuc-text-[#FF9B2A]">
                    {getTips()}
                  </div>
                  <div className="wbuc-flex-1 wbuc-overflow-hidden">
                    <TableEditor
                      ref={tableEditorRef}
                      initialConfig={initialConfig}
                      fields={fields}
                    />
                  </div>
                </div>
              )}
            </div>
          </Spin>
        </div>
      </Drawer>
      <Modal
        title="查看"
        width="85vw"
        open={preview}
        onCancel={() => setPreview(false)}
        loading={previewLoading}
      >
        <div className="wbuc-size-full wbuc-overflow-auto wbuc-p-5">
          <TableEditor.Viewer
            config={config}
            dataSource={withFormulasDataSource}
            className="wbuc-mx-auto wbuc-max-w-full"
          />
        </div>
      </Modal>
    </>
  );
};

SettlementEditor.Preview = Preview;
SettlementEditor.Viewer = Viewer;

export default SettlementEditor;

export { TableEditor };

export type {
  SettlementEditorProps,
  SettlementPreviewProps,
  SettlementViewerProps,
  SettlementViewerRef,
  TableEditorProps,
  TableEditorRef,
  TableViewerProps,
  TableViewerRef,
  TableEditorConfig,
};
